import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddLinkIcon from '@mui/icons-material/AddLink'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { AnalyticsIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import { selectEvents } from 'src/v3/content-pipeline/event-pipeline.slice'
import {
  InstructorLinkReportRow,
  clearInstructorLinkStatus,
  clearInstructorUnlinkStatus,
  fetchInstructorLinkReport,
  linkInstructorToUser,
  selectInstructorLinkError,
  selectInstructorLinkReport,
  selectInstructorLinkReportStatus,
  selectInstructorLinkStatus,
  selectInstructorUnlinkError,
  selectInstructorUnlinkStatus,
  unlinkInstructorUser,
} from 'src/v3/content-pipeline/instructor.slice'

dayjs.extend(utc)
dayjs.extend(timezone)

type Props = {
  onSchedule: (instructorID: string) => void
  onSentiment: (instructorID: string) => void
}

export default function InstructorLinkReportSection({
  onSchedule,
  onSentiment,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let report = useAppSelector(selectInstructorLinkReport)
  let reportStatus = useAppSelector(selectInstructorLinkReportStatus)
  let linkStatus = useAppSelector(selectInstructorLinkStatus)
  let linkError = useAppSelector(selectInstructorLinkError)
  let unlinkStatus = useAppSelector(selectInstructorUnlinkStatus)
  let unlinkError = useAppSelector(selectInstructorUnlinkError)
  let events = useAppSelector(selectEvents)
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  useAutoFetch(reportStatus, fetchInstructorLinkReport)

  let [linkingInstructor, setLinkingInstructor] =
    useState<InstructorLinkReportRow | null>(null)
  let [userID, setUserID] = useState('')
  let [expandedInstructorID, setExpandedInstructorID] = useState<string | null>(
    null,
  )

  let userTimeZone = dayjs.tz.guess()

  let eventsByInstructorID = useMemo(() => {
    let map = new Map<string, typeof events>()
    events.forEach((event) => {
      event.instructorIDs.forEach((instructorID) => {
        map.set(instructorID, [...(map.get(instructorID) ?? []), event])
      })
    })
    return map
  }, [events])

  useEffect(() => {
    if (linkStatus === 'fulfilled') {
      showSuccess('Instructor linked to user')
      setLinkingInstructor(null)
      setUserID('')
      dispatch(clearInstructorLinkStatus())
      dispatch(fetchInstructorLinkReport())
    } else if (linkStatus === 'rejected') {
      showError(linkError || 'Failed to link instructor to user')
      dispatch(clearInstructorLinkStatus())
    }
  }, [linkStatus])

  useEffect(() => {
    if (unlinkStatus === 'fulfilled') {
      showSuccess('Link removed')
      dispatch(clearInstructorUnlinkStatus())
      dispatch(fetchInstructorLinkReport())
    } else if (unlinkStatus === 'rejected') {
      showError(unlinkError || 'Failed to unlink instructor from user')
      dispatch(clearInstructorUnlinkStatus())
    }
  }, [unlinkStatus])

  let handleLinkSubmit = () => {
    if (!linkingInstructor || !userID.trim()) return
    dispatch(
      linkInstructorToUser({
        instructorID: linkingInstructor.instructorID,
        userID: userID.trim(),
      }),
    )
  }

  if (reportStatus === 'pending' || reportStatus === 'uninitialized') {
    return (
      <Card variant="outlined" sx={{ mt: 4, p: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Card>
    )
  }

  if (!report) return <></>

  return (
    <Card variant="outlined" sx={{ mt: 4, p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Instructor Links
      </Typography>

      {report.unlinkedInstructorsWithUpcomingSessions > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {report.unlinkedInstructorsWithUpcomingSessions} instructor
          {report.unlinkedInstructorsWithUpcomingSessions !== 1
            ? 's are'
            : ' is'}{' '}
          scheduled to teach an upcoming session but{' '}
          {report.unlinkedInstructorsWithUpcomingSessions !== 1 ? 'are' : 'is'}{' '}
          not linked to a user account — they won't receive a calendar invite.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Typography color="text.secondary" variant="body2">
          Total: <strong>{report.totalInstructors}</strong>
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Linked: <strong>{report.linkedInstructors}</strong>
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Unlinked: <strong>{report.unlinkedInstructors}</strong>
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Instructor</TableCell>
              <TableCell>System account</TableCell>
              <TableCell align="right">Associated courses</TableCell>
              <TableCell align="right">Sessions</TableCell>
              <TableCell align="right">Upcoming</TableCell>
              <TableCell>Next session</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.instructors.map((row) => {
              let isExpanded = expandedInstructorID === row.instructorID
              let associatedEvents =
                eventsByInstructorID.get(row.instructorID) ?? []

              return (
                <React.Fragment key={row.instructorID}>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.name}
                      </Typography>
                      {row.credentials && (
                        <Typography color="text.secondary" variant="caption">
                          {row.credentials}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          label={row.isLinked ? 'Linked' : 'Not linked'}
                          size="small"
                          color={row.isLinked ? 'success' : 'default'}
                          variant={row.isLinked ? 'filled' : 'outlined'}
                        />
                        {row.linkedUsers.map((linkedUser) => (
                          <Chip
                            key={linkedUser.instructorUserID}
                            label={`${linkedUser.name} (${linkedUser.email})`}
                            size="small"
                            onDelete={() =>
                              dispatch(
                                unlinkInstructorUser(
                                  linkedUser.instructorUserID,
                                ),
                              )
                            }
                            deleteIcon={<LinkOffIcon fontSize="small" />}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.25,
                        }}
                      >
                        {row.eventCount}
                        <IconButton
                          size="small"
                          aria-label={
                            isExpanded
                              ? `Hide ${row.name}'s courses`
                              : `Show ${row.name}'s courses`
                          }
                          onClick={() =>
                            setExpandedInstructorID(
                              isExpanded ? null : row.instructorID,
                            )
                          }
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{row.sessionCount}</TableCell>
                    <TableCell align="right">
                      {row.upcomingSessionCount}
                    </TableCell>
                    <TableCell>
                      {row.nextSessionTime
                        ? dayjs
                            .utc(row.nextSessionTime)
                            .tz(userTimeZone)
                            .format('MMM D, YYYY h:mm A')
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          component="button"
                          variant="body2"
                          color="primary"
                          onClick={() => onSchedule(row.instructorID)}
                          sx={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Schedule
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Run sentiment analysis for ${row.name}`}
                          onClick={() => onSentiment(row.instructorID)}
                        >
                          <AnalyticsIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Link ${row.name} to a user`}
                          onClick={() => setLinkingInstructor(row)}
                        >
                          <AddLinkIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}
                    >
                      <Collapse in={isExpanded} unmountOnExit>
                        <Box sx={{ py: 1.5 }}>
                          {associatedEvents.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                              No associated courses
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {associatedEvents.map((event) => (
                                <Chip
                                  key={event.id}
                                  label={event.name}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!linkingInstructor}
        onClose={
          linkStatus === 'pending'
            ? undefined
            : () => setLinkingInstructor(null)
        }
        fullWidth
        maxWidth="xs"
        aria-labelledby="link-instructor-title"
      >
        <DialogTitle id="link-instructor-title">
          Link {linkingInstructor?.name} to a user
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="User ID"
            value={userID}
            onChange={(e) => setUserID(e.target.value)}
            disabled={linkStatus === 'pending'}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLinkingInstructor(null)}
            disabled={linkStatus === 'pending'}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkSubmit}
            disabled={linkStatus === 'pending' || !userID.trim()}
            startIcon={
              linkStatus === 'pending' ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>

      {NotificationComponent}
    </Card>
  )
}
